import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';

const USERNAME_REGEX = /^[A-Za-z0-9_.]+$/;
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function loadTurnstileScript() {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve(window.turnstile);
    const existing = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      return;
    }
    const s = document.createElement('script');
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    document.head.appendChild(s);
  });
}

export default function AuthModal({ mode, onClose, onSwitchMode }) {
  const { login, register } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState({ turnstileEnabled: false, turnstileSiteKey: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const widgetRef = useRef(null);
  const widgetId = useRef(null);

  const isRegister = mode === 'register';

  useEffect(() => {
    api('/api/auth/config').then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!config.turnstileEnabled || !config.turnstileSiteKey) return;
    loadTurnstileScript().then((ts) => {
      if (cancelled || !widgetRef.current || !ts) return;
      if (widgetId.current) {
        try { ts.remove(widgetId.current); } catch {}
        widgetId.current = null;
      }
      widgetId.current = ts.render(widgetRef.current, {
        sitekey: config.turnstileSiteKey,
        theme: 'dark',
        callback: (tok) => setCaptchaToken(tok),
        'error-callback': () => setCaptchaToken(''),
        'expired-callback': () => setCaptchaToken(''),
      });
    });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current) {
        try { window.turnstile.remove(widgetId.current); } catch {}
        widgetId.current = null;
      }
    };
  }, [config, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!USERNAME_REGEX.test(username) || /\s/.test(username)) {
      setError('Username may only contain letters, numbers, _ and . (no spaces).');
      return;
    }
    if (isRegister && !PASSWORD_REGEX.test(password)) {
      setError('Password must be at least 8 chars, 1 uppercase, 1 number, 1 special symbol.');
      return;
    }
    if (config.turnstileEnabled && !captchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }

    setBusy(true);
    try {
      if (isRegister) await register(username, password, captchaToken);
      else await login(username, password, captchaToken);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed');
      if (window.turnstile && widgetId.current) {
        try { window.turnstile.reset(widgetId.current); } catch {}
      }
      setCaptchaToken('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <form className="modal" onSubmit={submit}>
        <h2>{isRegister ? 'Create Account' : 'Welcome Back'}</h2>

        <label>Username</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          maxLength={30}
          autoComplete="username"
          required
        />
        {isRegister && (
          <div className="hint">Letters, numbers, _ and . only. No spaces.</div>
        )}

        <label>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          maxLength={128}
          autoComplete={isRegister ? 'new-password' : 'current-password'}
          required
        />
        {isRegister && (
          <div className="hint">Min 8 chars, 1 uppercase, 1 number, 1 special symbol.</div>
        )}

        {config.turnstileEnabled && (
          <div className="turnstile-wrap" ref={widgetRef} />
        )}

        {error && <div className="error">{error}</div>}

        <div className="actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? '...' : isRegister ? 'Register' : 'Login'}
          </button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>

        <div className="switch">
          {isRegister ? (
            <>Already have an account? <button type="button" onClick={() => onSwitchMode('login')}>Login</button></>
          ) : (
            <>Don't have an account? <button type="button" onClick={() => onSwitchMode('register')}>Register</button></>
          )}
        </div>
      </form>
    </div>
  );
}
